import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
// 분석(AnalyticsDashboard)은 2-C-1에서 상세견적 마운트 제거. 파일은 보존(견적비교 재활용 예정).
import useIsMobile from "./hooks/useIsMobile";
import { apiFetch, apiSave, API_BASE_URL, fetchBrands, fetchFranchiseEstimates } from "./utils";
import { isBrandEnded } from "./utils/brandStatus";
import { friendlyErrorMessage, logFriendlyWarn } from "./ds/friendlyError";
import { TOP_TABS, getEnabledKindsForTab } from "./config/estimateMenuConfig";
import { fetchExtraWorkCandidates } from "./extraWorks";
import { periodDays } from "./contract/contractFields";


const STATUS_BADGE = {
  draft: "iggg-status iggg-status--draft",
  confirmed: "iggg-status iggg-status--confirm",
  settled: "iggg-status iggg-status--settle",
};

const STATUS_LABEL = {
  draft: "미확정",
  confirmed: "확정",
  settled: "정산완료",
};

// 종류 태그 — 인라인 솔리드 색상 뱃지를 iggg-kind mono outline 으로 통일
const KIND_TAG = {
  pre:        { l: "가견적",       cls: "iggg-kind iggg-kind--faint" },
  full:       { l: "본견적",       cls: "iggg-kind iggg-kind--accent" },
  simple:     { l: "간이",         cls: "iggg-kind iggg-kind--green" },
  simple_v2:  { l: "간이 v2",      cls: "iggg-kind iggg-kind--green" },
  balance:    { l: "잔금",         cls: "iggg-kind iggg-kind--blue" },
  franchise:  { l: "프랜차이즈",   cls: "iggg-kind iggg-kind--violet" },
};

function fmt(n) {
  if (!n) return "-";
  return Math.round(n / 10000).toLocaleString();
}

// 인라인 현장명 편집 — 클릭 시 input 으로 전환, Enter/blur 저장, ESC 취소.
// save: async (trimmedName) => void | (신규 이름).
// 기본 동작: /api/estimate/{id}/name PATCH. 다른 리소스는 save 콜백 주입.
function EditableName({ id, value, onSaved, className, save }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  // 외부 value 변경 시 초안 동기화 (다른 견적 수정 반영 등)
  useEffect(() => { setDraft(value || ""); }, [value]);

  const startEdit = (e) => {
    e.stopPropagation();
    setDraft(value || "");
    setEditing(true);
  };

  const cancel = () => {
    setDraft(value || "");
    setEditing(false);
  };

  const commit = async () => {
    const trimmed = (draft || "").trim();
    if (!trimmed || trimmed === (value || "")) {
      cancel();
      return;
    }
    setSaving(true);
    try {
      let finalName = trimmed;
      if (save) {
        const r = await save(trimmed);
        if (r && typeof r === "string") finalName = r;
      } else {
        const res = await apiFetch(`${API_BASE_URL}/api/estimate/${id}/name`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name: trimmed }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || `저장 실패 (${res.status})`);
        }
        const data = await res.json();
        finalName = data.name || trimmed;
      }
      setEditing(false);
      if (onSaved) onSaved(finalName);
    } catch (err) {
      logFriendlyWarn("EstimateList.renameSite", err);
      alert(friendlyErrorMessage(err, "현장명 저장에 실패했습니다."));
      cancel();
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={draft}
        disabled={saving}
        onChange={(e) => setDraft(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === "Enter") { e.preventDefault(); commit(); }
          else if (e.key === "Escape") { e.preventDefault(); cancel(); }
        }}
        onBlur={commit}
        className="bg-[var(--ds-canvas)] border border-[var(--ds-line)] rounded px-1.5 py-0.5 text-[var(--ds-ink)] outline-none w-full max-w-[260px]"
      />
    );
  }

  return (
    <span className={`inline-flex items-center gap-1 ${className || ""}`}>
      <span>{value || id}</span>
      <span
        onClick={startEdit}
        className="edit-icon"
        style={{ fontSize: 12, color: "var(--ds-mute)", cursor: 'pointer' }}
      >
        ✎
      </span>
    </span>
  );
}

// 견적날짜 추출 — 견적서에 표시되는 일자(가견적 estimate_date / 간이·프랜 project_info.date / 잔금 date).
// 목록 정렬·날짜는 이 값 기준 → 열어봐서 updated_at 이 갱신돼도 순서가 안 바뀐다.
function getEstimateDate(item) {
  if (!item) return "";
  return (
    item.estimate_date ||
    item.project_info?.date ||
    item.date ||
    item.meta?.date ||
    item.meta?.created_at ||
    item.created_at ||
    ""
  );
}

function fmtDate(s) {
  if (!s) return "-";
  try {
    const d = new Date(s);
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")}`;
  } catch {
    return s?.slice(0, 10) || "-";
  }
}

export default function EstimateList() {
  const navigate = useNavigate();
  // URL ?tab= 으로 탭 유지
  const _initTab = (() => { try { return new URLSearchParams(window.location.search).get("tab") || "general"; } catch { return "general"; } })();
  const [topTab, setTopTab] = useState(_initTab);
  // 상세견적: 상태 탭 제거(2-C-1) → 항상 전체 목록. 필터는 상단 견적-종류 탭이 담당.
  const [tab] = useState("all");
  // 잔금내역서 리스트
  const [balanceList, setBalanceList] = useState([]);
  const [balanceLoading, setBalanceLoading] = useState(false);
  // 생성예정: 계약서(ig-contract)는 작성됐으나 잔금내역서가 아직 없는 현장
  const [upcomingList, setUpcomingList] = useState([]);
  const [hiddenContracts, setHiddenContracts] = useState([]);  // 생성예정에서 숨긴 계약서
  const [showHidden, setShowHidden] = useState(false);          // 숨김 관리 펼침
  // 간이견적 리스트
  // 간이 탭: v1 + v2 병합 리스트 (_type: "simple" | "simple_v2")
  const [simpleList, setSimpleList] = useState([]);
  // 간이 탭 종류 필터 — 추가견적서(doc_kind=addendum)를 간이와 구분해 보기(2026-07-22)
  const [simpleKindFilter, setSimpleKindFilter] = useState("all"); // all | simple | addendum
  const simpleAddendumCount = useMemo(
    () => simpleList.filter((s) => s.doc_kind === "addendum").length, [simpleList]);
  const simpleShown = useMemo(() => (
    simpleKindFilter === "all"
      ? simpleList
      : simpleList.filter((s) => (s.doc_kind === "addendum") === (simpleKindFilter === "addendum"))
  ), [simpleList, simpleKindFilter]);
  const [simpleLoading, setSimpleLoading] = useState(false);
  const [extraWorks, setExtraWorks] = useState([]);  // 현장관리 추가공사 후보
  // 프랜차이즈
  const [brandList, setBrandList] = useState([]);
  const [franchiseList, setFranchiseList] = useState([]);
  const [franchiseLoading, setFranchiseLoading] = useState(false);
  // 전체 탭
  const [mergedList, setMergedList] = useState([]);
  const [mergedLoading, setMergedLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");  // 상세견적 목록 현장명 검색
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // 모바일 여부 (셸 DsTopBar/하단탭이 앱 네비 담당 — 대시보드는 탭+목록만)
  // 셸 전환점 lg(1024)와 일치 — 폴드 중간폭(<1024)에서 사이드바 대신 하단탭+풀폭 카드.
  const isMobile = useIsMobile(1024);

  // 헌법 기반 헤더 우측 버튼 — topTab='all'은 진입점 없음, 그 외는 해당 탭 enabled 종류 직접 진입 (데스크톱)
  const headerKinds = topTab === "all" ? [] : getEnabledKindsForTab(topTab);

  // 간이/잔금 현장명 저장 (EditableName.save 콜백 주입용)
  // _type: "simple" (v1) | "simple_v2" (v2) — 엔드포인트 분기
  const saveSimpleName = useCallback(async (id, newName, _type = "simple") => {
    const endpoint = _type === "simple_v2"
      ? `${API_BASE_URL}/api/simple-estimates-v2/${id}`
      : `${API_BASE_URL}/api/simple-estimates/${id}`;
    const res = await apiFetch(endpoint, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ project_info: { name: newName } }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `저장 실패 (${res.status})`);
    }
    const data = await res.json();
    return (data.project_info && data.project_info.name) || newName;
  }, []);

  const saveBalanceName = useCallback(async (id, newName) => {
    const res = await apiFetch(`${API_BASE_URL}/api/balance-sheets/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ project_name: newName }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `저장 실패 (${res.status})`);
    }
    const data = await res.json();
    return data.project_name || newName;
  }, []);

  // 상세견적 탭 현장명 저장 — v2(quick_estimates_v2)는 PUT /quick-v2/{id} {project_name},
  // legacy(estimates)는 PATCH /api/estimate/{id}/name. 분기 안 하면 v2(manual 복사본)가
  // legacy 엔드포인트로 가서 "현장명 저장 실패"가 났음.
  const saveGeneralName = useCallback(async (id, newName, isV2) => {
    if (isV2) {
      const res = await apiFetch(`${API_BASE_URL}/api/estimate/quick-v2/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ project_name: newName }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `저장 실패 (${res.status})`);
      }
      const data = await res.json();
      return (data && data.project_name) || newName;
    }
    const res = await apiFetch(`${API_BASE_URL}/api/estimate/${id}/name`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: newName }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `저장 실패 (${res.status})`);
    }
    const data = await res.json();
    return data.name || newName;
  }, []);

  // 확정 깃발 토글 — status 'confirmed'↔'draft'. v2=PUT quick-v2, legacy=PATCH /status.
  //   같은 현장 여러 시안 중 어느 게 확정인지 표시(목록·정산 견적연결 공용).
  const toggleConfirmed = useCallback(async (e, item, isV2) => {
    e.stopPropagation();
    const cur = item?.meta?.status || item?.status || "draft";
    const next = cur === "confirmed" ? "draft" : "confirmed";
    // 낙관적 갱신
    setItems((prev) => prev.map((it) => it.id === item.id
      ? { ...it, status: next, meta: { ...(it.meta || {}), status: next } } : it));
    try {
      const url = isV2
        ? `${API_BASE_URL}/api/estimate/quick-v2/${item.id}`
        : `${API_BASE_URL}/api/estimate/${item.id}/status`;
      const res = await apiFetch(url, {
        method: isV2 ? "PUT" : "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: next }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    } catch (err) {
      logFriendlyWarn("EstimateList.toggleConfirmed", err);
      // 롤백
      setItems((prev) => prev.map((it) => it.id === item.id
        ? { ...it, status: cur, meta: { ...(it.meta || {}), status: cur } } : it));
    }
  }, []);

  // 생성예정 클릭 → 계약금액으로 잔금내역서 생성 후 이동 (중복 시 기존 것으로 이동)
  const createFromContract = useCallback(async (contractId) => {
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/balance-sheets/from-contract`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ contract_id: contractId }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `생성 실패 (${res.status})`);
      }
      const data = await res.json();
      navigate(`/balance/${data.id}`);
    } catch (e) {
      alert(friendlyErrorMessage(e, "잔금내역서 생성에 실패했습니다."));
    }
  }, [navigate]);

  // 생성예정에서 숨김 / 되돌리기 — apiSave(#83 규약): 서버 4xx/5xx 면 던진다.
  // 예전엔 res.ok 를 안 봐서 서버가 거절해도 화면만 숨겨졌고, 새로고침하면
  // 부활했다(#215). 실패 시 낙관 갱신을 **롤백**하고 사용자에게 알린다.
  const hideContract = useCallback(async (contractId, siteName) => {
    let removed = [];                                      // 롤백용 — 지운 행만 캡처
    setUpcomingList(prev => {
      removed = prev.filter(u => u.contract_id === contractId);
      return prev.filter(u => u.contract_id !== contractId);
    });
    setHiddenContracts(prev => prev.some(h => h.contract_id === contractId)
      ? prev : [...prev, { contract_id: contractId, site_name: siteName }]);
    try {
      await apiSave(`${API_BASE_URL}/api/balance-sheets/hide-contract`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ contract_id: contractId, site_name: siteName }),
      });
    } catch (e) {
      logFriendlyWarn("hideContract", e);
      setUpcomingList(prev => [...prev, ...removed]);      // 롤백 — 화면≠서버 금지
      setHiddenContracts(prev => prev.filter(h => h.contract_id !== contractId));
      alert(friendlyErrorMessage(e, "숨김이 저장되지 않았습니다 — 새로고침하면 다시 나타납니다."));
    }
  }, []);

  const unhideContract = useCallback(async (contractId, siteName) => {
    setHiddenContracts(prev => prev.filter(h => h.contract_id !== contractId));
    try {
      await apiSave(`${API_BASE_URL}/api/balance-sheets/unhide-contract`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ contract_id: contractId }),
      });
      // upcoming 재조회 (복귀 반영)
      const r = await apiFetch(`${API_BASE_URL}/api/balance-sheets/upcoming`);
      if (r.ok) setUpcomingList(await r.json());
    } catch (e) {
      logFriendlyWarn("unhideContract", e);
      setHiddenContracts(prev => prev.some(h => h.contract_id === contractId)
        ? prev : [...prev, { contract_id: contractId, site_name: siteName }]);  // 롤백
      alert(friendlyErrorMessage(e, "복귀가 저장되지 않았습니다."));
    }
  }, []);

  const load = useCallback(async (status) => {
    setLoading(true);
    setError("");
    try {
      const qs = status !== "all" ? `?status=${status}` : "";
      // 가견적은 두 컬렉션에 나뉘어 있다: legacy(estimates) + v2(quick_estimates_v2).
      // 과거엔 estimates(/api/estimate/list)만 조회해 신규 v2 가견적이 목록에서 누락됐다.
      const [legacyData, v2Data] = await Promise.all([
        apiFetch(`${API_BASE_URL}/api/estimate/list${qs}`).then(r => r.ok ? r.json() : []).catch(() => []),
        apiFetch(`${API_BASE_URL}/api/estimate/quick-v2/list`).then(r => r.ok ? r.json() : {}).catch(() => ({})),
      ]);
      const legacy = Array.isArray(legacyData) ? legacyData : [];
      const v2 = ((v2Data && v2Data.items) || []).map(e => ({
        id: e.id,
        // estimates 렌더와 동일 필드로 매핑. total_amount 는 legacy 와 동일하게 '원' 단위
        // (렌더의 fmt/Math.round(/10000) 가 만원변환 담당). area_pyeong 으로 평당가 산출.
        project_info: { name: e.project_name, total_amount: e.grand_total || 0, area_pyeong: e.area_pyeong || 0 },
        meta: { type: "quick_v2", updated_at: e.updated_at, status: e.status },
        updated_at: e.updated_at,
        estimate_date: e.estimate_date,
        estimate_type: e.estimate_type,
        business_type: e.business_type,
        _v2: true,
      }));
      const merged = [...v2, ...legacy].sort((a, b) => {
        const da = getEstimateDate(a);
        const db = getEstimateDate(b);
        return String(db).localeCompare(String(da));
      });
      setItems(merged);
    } catch (e) {
      logFriendlyWarn("EstimateList.load", e);
      setError(friendlyErrorMessage(e, "견적 목록을 불러오지 못했습니다."));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(tab);
  }, [tab, load]);

  // 브랜드 목록 — 마운트 시 1회 (franchise 탭 여부 무관)
  useEffect(() => {
    fetchBrands()
      .then(setBrandList)
      .catch(() => setBrandList([]));
  }, []);

  // 프랜차이즈 견적 리스트 fetch
  useEffect(() => {
    if (topTab !== "franchise") return;
    setFranchiseLoading(true);
    fetchFranchiseEstimates()
      // 전체 탭과 동일한 최신순(updated_at desc) 정렬 — 프랜탭만 미정렬이라 순서 불일치하던 것 수정
      .then(list => setFranchiseList(
        [...(Array.isArray(list) ? list : [])].sort((a, b) => {
          const da = getEstimateDate(a);
          const db = getEstimateDate(b);
          return db.localeCompare(da);
        })
      ))
      .catch(() => setFranchiseList([]))
      .finally(() => setFranchiseLoading(false));
  }, [topTab]);

  // 잔금내역서 리스트 + 생성예정(계약서 연동) fetch
  useEffect(() => {
    if (topTab !== "balance") return;
    setBalanceLoading(true);
    apiFetch(`${API_BASE_URL}/api/balance-sheets`)
      .then(r => r.ok ? r.json() : [])
      .then(d => setBalanceList(Array.isArray(d) ? d : d.items || []))
      .catch(() => setBalanceList([]))
      .finally(() => setBalanceLoading(false));
    apiFetch(`${API_BASE_URL}/api/balance-sheets/upcoming`)
      .then(r => r.ok ? r.json() : [])
      .then(d => setUpcomingList(Array.isArray(d) ? d : []))
      .catch(() => setUpcomingList([]));
    apiFetch(`${API_BASE_URL}/api/balance-sheets/hidden`)
      .then(r => r.ok ? r.json() : [])
      .then(d => setHiddenContracts(Array.isArray(d) ? d : []))
      .catch(() => setHiddenContracts([]));
  }, [topTab]);

  // 간이견적 리스트 fetch — v1 + v2 병합 (상세 탭의 가견적/본견적/v2 패턴과 동일)
  useEffect(() => {
    if (topTab !== "simple") return;
    setSimpleLoading(true);
    const fetchJson = (url) => apiFetch(url).then(r => r.ok ? r.json() : []).catch(() => []);
    Promise.all([
      fetchJson(`${API_BASE_URL}/api/simple-estimates`)
        .then(d => {
          const list = Array.isArray(d) ? d : Array.isArray(d?.items) ? d.items : Array.isArray(d?.data) ? d.data : [];
          return list.map(e => ({ ...e, _type: "simple" }));
        }),
      fetchJson(`${API_BASE_URL}/api/simple-estimates-v2`)
        .then(d => {
          const list = Array.isArray(d) ? d : Array.isArray(d?.items) ? d.items : [];
          return list.map(e => ({ ...e, _type: "simple_v2" }));
        }),
    ])
      .then(([v1, v2]) => {
        const merged = [...v1, ...v2].sort((a, b) => {
          const da = getEstimateDate(a);
          const db = getEstimateDate(b);
          return db.localeCompare(da);
        });
        setSimpleList(merged);
      })
      .catch(() => setSimpleList([]))
      .finally(() => setSimpleLoading(false));
  }, [topTab]);

  // 현장관리(iggg-sites)의 추가공사 후보 — 간이견적 탭에서만
  useEffect(() => {
    if (topTab !== "simple") return;
    fetchExtraWorkCandidates().then(setExtraWorks).catch(() => setExtraWorks([]));
  }, [topTab]);

  // 전체 탭 병합 로드
  useEffect(() => {
    if (topTab !== "all") return;
    setMergedLoading(true);
    const fetchJson = (url) => apiFetch(url).then(r => r.ok ? r.json() : []).catch(() => []);
    Promise.all([
      fetchJson(`${API_BASE_URL}/api/estimate/list`).then(d => (Array.isArray(d) ? d : d.items || []).map(e => ({ ...e, _type: e.meta?.type === "quick" ? "pre" : "full" }))),
      fetchJson(`${API_BASE_URL}/api/simple-estimates`).then(d => (Array.isArray(d) ? d : d.items || d.data || []).map(e => ({ ...e, _type: "simple" }))),
      fetchJson(`${API_BASE_URL}/api/simple-estimates-v2`).then(d => (Array.isArray(d) ? d : d.items || []).map(e => ({ ...e, _type: "simple_v2" }))),
      fetchJson(`${API_BASE_URL}/api/balance-sheets`).then(d => (Array.isArray(d) ? d : d.items || []).map(e => ({ ...e, _type: "balance" }))),
      fetchJson(`${API_BASE_URL}/api/franchise-estimates`).then(d => (Array.isArray(d) ? d : d.items || d.data || []).map(e => ({ ...e, _type: "franchise" }))),
    ]).then(([est, simple, simple_v2, balance, franchise]) => {
      const merged = [...est, ...simple, ...simple_v2, ...balance, ...franchise].sort((a, b) => {
        const da = getEstimateDate(a);
        const db = getEstimateDate(b);
        return db.localeCompare(da);
      });
      setMergedList(merged);
    }).finally(() => setMergedLoading(false));
  }, [topTab]);

  const handleRowClick = (item) => {
    const id = item?.id;
    if (!id) return;
    // 가견적 v2(quick_estimates_v2)는 v2 에디터로, legacy 가견적은 채팅, 정식견적은 워크플로우 에디터로
    if (item?._v2 || item?.meta?.type === "quick_v2") {
      navigate(`/estimate/quick/${id}`);
    } else if (item?.meta?.type === "quick") {
      navigate(`/estimate/quick-legacy?id=${id}`);
    } else {
      navigate(`/estimate/${id}`);
    }
  };

  // 현장명 변경 후 리스트 즉시 반영 (재요청 없이 로컬 state 업데이트)
  const handleNameUpdate = (id, newName) => {
    setItems((prev) =>
      prev.map((it) =>
        it.id === id
          ? {
              ...it,
              project_info: { ...(it.project_info || {}), name: newName },
              meta: { ...(it.meta || {}), name: newName },
            }
          : it
      )
    );
  };

  // 복제 → 수동견적 생성. 어떤 상세견적(quick_v2)이든 deep-copy 후 estimate_type='manual'
  // 강제, 새 수동견적 에디터로 이동. legacy(estimates 컬렉션)는 copy 미지원이라 버튼 미노출.
  const handleDuplicate = async (e, id) => {
    e.stopPropagation();
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/estimate/quick-v2/${id}/copy`, {
        method: "POST",
        body: JSON.stringify({ estimate_type: "manual" }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || err.detail || `복제 실패 (${res.status})`);
      }
      const doc = await res.json();
      navigate(`/estimate/quick/${doc.id}`);
    } catch (err) {
      alert(friendlyErrorMessage(err, "복제에 실패했습니다."));
    }
  };

  const handleDelete = async (e, id, siteName, isV2 = false) => {
    e.stopPropagation();
    const ok = window.confirm(
      `'${siteName || id}' 견적을 삭제하시겠습니까?\n(휴지통에서 7일간 복원 가능)`
    );
    if (!ok) return;
    try {
      // v2 가견적은 quick_estimates_v2 컬렉션 — 전용 삭제 엔드포인트 사용 (legacy estimates 와 분리)
      const url = isV2
        ? `${API_BASE_URL}/api/estimate/quick-v2/${id}`
        : `${API_BASE_URL}/api/estimate/${id}`;
      const res = await apiFetch(url, { method: "DELETE" });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `삭제 실패 (${res.status})`);
      }
      load(tab);
    } catch (err) {
      alert(friendlyErrorMessage(err, "삭제에 실패했습니다."));
    }
  };

  return (
    <div
      className="iggg-shell iggg-shell--narrow min-h-screen iggg-anim-fade"
      style={{
        fontFamily: "var(--ff-body, 'Pretendard Variable','Pretendard','Noto Sans KR',sans-serif)",
        color: "var(--iggg-fg)",
        fontSize: "var(--fz-body, 14px)",
        lineHeight: 1.55,
      }}
    >
      {/* 헤더 */}
      {isMobile ? (
        <div className="sticky top-[56px] z-20" style={{ background: "var(--iggg-bg)" }}>
          {/* 현재 탭의 생성 버튼(＋잔금내역서 등) — 셸 ＋는 잔금 생성을 안 다뤄 여기서 직접 제공 */}
          {headerKinds.length > 0 && (
            <div className="flex justify-end flex-wrap gap-2 px-3 pt-2 pb-1">
              {headerKinds.map((kind) => (
                <button key={kind.key} onClick={() => navigate(kind.path)} className="ds-btn ds-btn-primary ds-btn-sm">
                  + {kind.label}
                </button>
              ))}
            </div>
          )}
          {/* 상위 탭 — 로고·테마는 셸(DsTopBar+하단탭)이 담당 */}
          <div className="iggg-tabs">
            {TOP_TABS.map(t => (
              <button key={t.key}
                onClick={() => { setTopTab(t.key); window.history.replaceState(null, "", `/dashboard?tab=${t.key}`); }}
                aria-selected={topTab === t.key}
                className={`iggg-tab ${topTab === t.key ? "is-active" : ""}`}
              >{t.shortLabel}</button>
            ))}
          </div>
        </div>
      ) : (
      <div className="py-4 flex flex-col gap-3 sticky top-0 z-20"
           style={{
             background: "var(--iggg-bg)",
             borderBottom: "1px dotted var(--iggg-line-soft)",
           }}>
        {/* 1행: 새 견적 kind 버튼만 (로고·테마·툴은 셸 사이드바가 담당) */}
        {headerKinds.length > 0 && (
          <div className="flex items-center justify-end">
            <div className="flex items-center gap-2 flex-wrap">
              {headerKinds.map((kind) => (
                <button
                  key={kind.key}
                  onClick={() => navigate(kind.path)}
                  className="ds-btn ds-btn-primary"
                >
                  + {kind.label}
                </button>
              ))}
            </div>
          </div>
        )}
        {/* 2행: 최상단 탭 — IGGG reference 톤 (데스크톱) */}
        <div className="iggg-tabs" style={{ borderBottom: "none" }}>
          {TOP_TABS.map(t => (
            <button key={t.key}
              onClick={() => { setTopTab(t.key); window.history.replaceState(null, "", `/dashboard?tab=${t.key}`); }}
              aria-selected={topTab === t.key}
              className={`iggg-tab ${topTab === t.key ? "is-active" : ""}`}
              style={{ flex: "0 0 auto" }}
            >{t.label}</button>
          ))}
        </div>
      </div>
      )}

      {/* 탭 콘텐츠 영역 */}
      <div>

      {/* ═══ 상세견적 탭 — 집계·상태탭 제거(2-C-1), 리스트만 ═══ */}
      {topTab === "general" && <>

      <div className="p-3 pb-20">
        {/* 오류 */}
        {error && (
          <div className="bg-[var(--ds-pill-danger-bg)] border border-[var(--ds-danger)] rounded-lg p-3 text-sm text-[var(--ds-danger)] mb-3">
            {error}
          </div>
        )}

        {/* 로딩 */}
        {loading && (
          <div className="flex items-center justify-center py-16 text-[var(--ds-mute)] text-sm">
            불러오는 중...
          </div>
        )}

        {/* 빈 상태 — IGGG empty */}
        {!loading && !error && items.length === 0 && (
          <div className="iggg-empty iggg-anim-fade">
            <div className="iggg-empty__mark">◇</div>
            <div>
              <div className="iggg-empty__title">저장된 견적이 없습니다</div>
              <div className="iggg-empty__meta" style={{ marginTop: 4 }}>
                start a new estimate
              </div>
            </div>
            {/* (전수검증) /estimate/new 는 폐기 라우트 → 견적 만들기로 */}
            <button
              onClick={() => navigate("/create")}
              className="ds-btn ds-btn-primary"
            >
              + 견적 작성
            </button>
          </div>
        )}

        {/* 검색 (현장명) */}
        {!loading && items.length > 0 && (() => {
          const q = search.trim().toLowerCase();
          const filteredItems = q
            ? items.filter((it) => ((it.project_info?.name || "")).toLowerCase().includes(q))
            : items;
          return (
        <>
            <div style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="현장명 검색"
                style={{ flex: "0 1 280px", padding: "8px 12px", fontSize: 13, borderRadius: 8, boxSizing: "border-box",
                  /* (전수검증) 다크테마 흰배경 고정 해소 */
                  border: "1px solid var(--ds-line)", background: "var(--ds-canvas)", color: "var(--ds-ink)" }}
              />
              {q && (
                <span style={{ fontSize: 12, color: "var(--iggg-fg-mute)" }}>
                  {filteredItems.length}건
                </span>
              )}
            </div>

            {/* 데스크톱 테이블 (md 이상) — IGGG table */}
            <div style={{ display: isMobile ? "none" : "block" }}>
              <table className="iggg-table iggg-table--ref">
                <thead>
                  <tr>
                    <th style={{ textAlign: "left" }}>날짜 · 현장명</th>
                    <th style={{ textAlign: "right" }}>총액 (만원)</th>
                    <th style={{ textAlign: "right" }}>평수</th>
                    <th style={{ textAlign: "right" }}>평당가 (만/평)</th>
                    <th style={{ textAlign: "right" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.map((item) => {
                    const pi = item.project_info || {};
                    const meta = item.meta || {};
                    const status = meta.status || item.status || "draft";
                    const totalAmt = pi.total_amount || 0;
                    const areaPy = pi.area_pyeong || (pi.area_m2 ? pi.area_m2 / 3.305785 : 0);
                    const perPy = areaPy > 0 ? totalAmt / areaPy : 0;
                    const dateStr = fmtDate(getEstimateDate(item));
                    const changeCnt = item.change_count ?? (item.change_log?.length ?? 0);
                    const stm = item.settlement_summary;
                    let stmCell = "-";
                    let stmCls = "text-[var(--ds-mute)]";
                    if (stm) {
                      if (status === "settled" && stm.variance_rate !== null && stm.variance_rate !== undefined) {
                        const pct = stm.variance_rate * 100;
                        const abs = Math.abs(pct);
                        stmCls = abs <= 10 ? "text-[var(--ds-success)]" : abs <= 20 ? "text-[var(--ds-ink)]" : "text-[var(--ds-danger)]";
                        stmCell = `${pct >= 0 ? "+" : ""}${pct.toFixed(1)}%`;
                      } else if (stm.completion) {
                        stmCell = stm.completion;
                        stmCls = "text-[var(--ds-body)]";
                      }
                    }

                    return (
                      <tr
                        key={item.id}
                        onClick={() => handleRowClick(item)}
                        className="estimate-row"
                      >
                        <td style={{ fontWeight: 500 }}>
                          <span className="inline-flex items-center" style={{ gap: "0.4rem" }}>
                            <span style={{ fontFamily: "var(--ff-mono)", fontSize: "0.72rem", color: "var(--iggg-fg-mute)", whiteSpace: "nowrap" }}>{dateStr}</span>
                            {/* 이모지(🚩)는 색 고정이라 텍스트 글리프 ⚑ 사용. 진녹색 고정(--ds-success는 형광톤이라 미사용) */}
                            {status === "confirmed" && (
                              <span title="확정 견적" style={{ color: "#166534", fontSize: "1rem" }}>⚑</span>
                            )}
                            <EditableName
                              id={item.id}
                              value={pi.name}
                              save={(v) => saveGeneralName(item.id, v, item._v2 || item?.meta?.type === "quick_v2")}
                              onSaved={(nm) => handleNameUpdate(item.id, nm)}
                            />
                            {meta?.type === "quick" && (
                              <span className="iggg-kind iggg-kind--faint">LEGACY</span>
                            )}
                          </span>
                        </td>
                        <td className="num-mono" style={{ textAlign: "right", fontWeight: 600 }}>
                          {totalAmt > 0 ? fmt(totalAmt) : "-"}
                        </td>
                        <td className="num-mono" style={{ textAlign: "right", color: "var(--iggg-fg-mute)" }}>
                          {areaPy > 0 ? `${Math.round(areaPy)}평` : "-"}
                        </td>
                        <td className="num-mono" style={{ textAlign: "right", color: "var(--iggg-accent)" }}>
                          {perPy > 0 ? fmt(perPy) : "-"}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          <span className="inline-flex items-center" style={{ gap: "0.4rem" }}>
                            <button
                              onClick={(e) => toggleConfirmed(e, item, item._v2 || item?.meta?.type === "quick_v2")}
                              className="iggg-btn iggg-btn--ghost iggg-btn--cap"
                              title={status === "confirmed" ? "확정 해제" : "확정 표시(같은 현장 여러 시안 중 확정본)"}
                              style={status === "confirmed"
                                ? { color: "#166534", borderColor: "#166534" }
                                : { opacity: 0.55 }}
                            >
                              {status === "confirmed" ? "⚑ 확정" : "⚐ 확정"}
                            </button>
                            {(item._v2 || item?.meta?.type === "quick_v2") && (
                              <button
                                onClick={(e) => handleDuplicate(e, item.id)}
                                className="iggg-btn iggg-btn--ghost iggg-btn--cap"
                                title="복제해 수동견적으로 가공"
                              >
                                복제
                              </button>
                            )}
                            <button
                              onClick={(e) => handleDelete(e, item.id, pi.name, item._v2)}
                              className="iggg-btn iggg-btn--ghost iggg-btn--cap"
                              style={{ color: "var(--iggg-danger)", borderColor: "rgba(255,107,94,0.35)" }}
                            >
                              삭제
                            </button>
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* 모바일 카드 (isMobile — 헤더와 동일 분기로 통일, md: 불일치 제거) */}
            <div className="flex flex-col gap-2" style={{ display: isMobile ? "flex" : "none" }}>
              {filteredItems.map((item) => {
                const pi = item.project_info || {};
                const meta = item.meta || {};
                const status = meta.status || item.status || "draft";
                const totalAmt = pi.total_amount || 0;
                const areaPy = pi.area_pyeong || (pi.area_m2 ? pi.area_m2 / 3.305785 : 0);
                const perPy = areaPy > 0 ? totalAmt / areaPy : 0;
                const dateStr = fmtDate(getEstimateDate(item));
                const changeCnt = item.change_count ?? (item.change_log?.length ?? 0);
                const stm = item.settlement_summary;
                let stmText = null;
                let stmCls = "text-[var(--ds-mute)]";
                if (stm) {
                  if (status === "settled" && stm.variance_rate !== null && stm.variance_rate !== undefined) {
                    const pct = stm.variance_rate * 100;
                    const abs = Math.abs(pct);
                    stmCls = abs <= 10 ? "text-[var(--ds-success)]" : abs <= 20 ? "text-[var(--ds-ink)]" : "text-[var(--ds-danger)]";
                    stmText = `정산 ${pct >= 0 ? "+" : ""}${pct.toFixed(1)}%`;
                  } else if (stm.completion) {
                    stmText = `정산 ${stm.completion}`;
                    stmCls = "text-[var(--ds-body)]";
                  }
                }

                return (
                  <div
                    key={item.id}
                    onClick={() => handleRowClick(item)}
                    className="estimate-row iggg-card iggg-card--interactive iggg-card--beige"
                    style={{ cursor: "pointer" }}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="iggg-card-title flex items-center gap-1.5 flex-wrap">
                        <EditableName
                          id={item.id}
                          value={pi.name}
                          save={(v) => saveGeneralName(item.id, v, item._v2 || item?.meta?.type === "quick_v2")}
                          onSaved={(nm) => handleNameUpdate(item.id, nm)}
                        />
                        {meta?.type === "quick" && (
                          <span className="iggg-kind iggg-kind--faint">LEGACY</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className={STATUS_BADGE[status] || STATUS_BADGE.draft}>
                          {STATUS_LABEL[status] || status}
                        </span>
                        {(item._v2 || item?.meta?.type === "quick_v2") && (
                          <button
                            onClick={(e) => handleDuplicate(e, item.id)}
                            className="iggg-btn iggg-btn--ghost iggg-btn--cap"
                            title="복제해 수동견적으로 가공"
                          >
                            복제
                          </button>
                        )}
                        <button
                          onClick={(e) => handleDelete(e, item.id, pi.name, item._v2)}
                          className="iggg-btn iggg-btn--ghost iggg-btn--cap"
                          style={{ color: "var(--iggg-danger)", borderColor: "rgba(255,107,94,0.35)" }}
                        >
                          삭제
                        </button>
                      </div>
                    </div>
                    <div className="iggg-card-meta flex items-center gap-2 mb-2">
                      <span>{pi.business_type || "-"}</span>
                      <span>·</span>
                      <span>{dateStr}</span>
                      {changeCnt > 0 && (
                        <>
                          <span>·</span>
                          <span>변경 {changeCnt}건</span>
                        </>
                      )}
                      {stmText && (
                        <>
                          <span>·</span>
                          <span className={`font-semibold ${stmCls}`}>{stmText}</span>
                        </>
                      )}
                    </div>
                    <div className="flex items-center gap-6">
                      <div>
                        <div className="iggg-card-meta">총액 · total</div>
                        <div className="num-mono" style={{ color: "var(--iggg-fg)", fontWeight: 700, fontSize: "0.9375rem", marginTop: 2 }}>
                          {totalAmt > 0 ? `${fmt(totalAmt)}만원` : "-"}
                        </div>
                      </div>
                      <div>
                        <div className="iggg-card-meta">평당가 · per py</div>
                        <div className="num-mono" style={{ color: "var(--iggg-accent)", fontWeight: 700, fontSize: "0.9375rem", marginTop: 2 }}>
                          {perPy > 0 ? `${fmt(perPy)}만/평` : "-"}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
          );
        })()}
      </div>
      </>}

      {/* ═══ 프랜차이즈 견적 탭 ═══ */}
      {topTab === "franchise" && (
        <div className="p-3 pb-20">
          {/* 브랜드 0건: 브랜드 관리 유도 */}
          {brandList.length === 0 ? (
            <div className="iggg-empty">
              <div className="iggg-empty__mark">◇</div>
              <div>
                <div className="iggg-empty__title">프랜차이즈 견적</div>
                <div className="iggg-empty__meta" style={{ marginTop: 6, textTransform: "none", letterSpacing: 0, fontFamily: "var(--ff-body)", color: "var(--iggg-fg-mute)", maxWidth: 360 }}>
                  먼저 [브랜드 관리]에서 브랜드를 등록해야 프랜차이즈 견적을 작성할 수 있습니다.
                </div>
              </div>
              <button
                onClick={() => navigate("/brands")}
                className="ds-btn ds-btn-primary"
              >
                브랜드 관리로 이동
              </button>
            </div>
          ) : (
            <>
              {/* 브랜드 카드 그리드 */}
              <div className="iggg-section-header">
                <span className="iggg-section-num">01</span>
                <span className="iggg-section-title">브랜드</span>
                <span className="iggg-section-sub">brands</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 mb-6">
                {brandList.map((b) => {
                  const brandEsts = franchiseList.filter((e) => e.brand_id === b.id);
                  const avgPerPy = brandEsts.length > 0
                    ? Math.round(
                        brandEsts.reduce((s, e) => {
                          const total = e.project_info?.total_amount || e.grand_total || 0;
                          const py = e.main_work?.area_pyeong || 0;
                          return s + (py > 0 ? total / py : 0);
                        }, 0) / brandEsts.length / 10000
                      )
                    : null;
                  return (
                    <div
                      key={b.id}
                      onClick={() => navigate(`/franchise?brand=${b.id}`)}
                      className="iggg-card iggg-card--interactive iggg-card--compact"
                      style={{ cursor: "pointer", ...(isBrandEnded(b) ? { opacity: 0.55 } : {}) }}
                    >
                      {b.logo_url ? (
                        <img
                          src={b.logo_url}
                          alt={b.name}
                          className="w-10 h-10 object-contain bg-white mb-2"
                          style={{ borderRadius: 4 }}
                        />
                      ) : (
                        <div
                          className="w-10 h-10 mb-2 flex items-center justify-center"
                          style={{ background: "var(--iggg-bg-input)", border: "1px solid var(--iggg-line-soft)", borderRadius: 4, fontSize: "1rem", color: "var(--iggg-fg-mute)" }}
                        >
                          ◇
                        </div>
                      )}
                      <div className="iggg-card-title" style={{ fontSize: "0.875rem", lineHeight: 1.3 }}>
                        {b.name}
                        {/* 계약종료 브랜드 — 목록에서 한눈에 구분(2026-09-02) */}
                        {isBrandEnded(b) && (
                          <span className="iggg-kind iggg-kind--faint" style={{ marginLeft: 6, verticalAlign: "middle" }}>종료</span>
                        )}
                      </div>
                      <div className="iggg-card-meta" style={{ marginTop: 2 }}>
                        견적 {brandEsts.length}건
                        {avgPerPy ? ` · 평당 ${avgPerPy}만` : ""}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* 최근 프랜차이즈 견적 리스트 */}
              <div className="iggg-section-header">
                <span className="iggg-section-num">02</span>
                <span className="iggg-section-title">최근 프랜차이즈 견적</span>
                <span className="iggg-section-sub">recent</span>
              </div>
              {franchiseLoading && (
                <div className="text-center py-8 iggg-empty__meta">불러오는 중...</div>
              )}
              {!franchiseLoading && franchiseList.length === 0 && (
                <div className="text-center py-8 iggg-empty__meta">아직 프랜차이즈 견적이 없습니다</div>
              )}
              {!franchiseLoading && franchiseList.length > 0 && (
                isMobile ? (
                  <div className="flex flex-col gap-2">
                    {franchiseList.map((e, i) => {
                      const pi = e.project_info || {};
                      const total = pi.total_amount || e.grand_total || 0;
                      const py = e.main_work?.area_pyeong || 0;
                      return (
                        <div
                          key={e.id || i}
                          onClick={() => navigate(`/estimate/franchise/${e.id}`)}
                          className="iggg-card iggg-card--interactive"
                          style={{ cursor: "pointer" }}
                        >
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <div className="iggg-card-title">
                              {pi.name || e.brand_name_snapshot || "-"}
                            </div>
                            <span className="iggg-kind iggg-kind--violet shrink-0">프랜차이즈</span>
                          </div>
                          <div className="iggg-card-meta mb-2">
                            <span style={{ fontFamily: "var(--ff-mono)" }}>{fmtDate(getEstimateDate(e))}</span> · {e.brand_name_snapshot || "-"}
                            {py > 0 && ` · ${py}평`}
                          </div>
                          <div className="num-mono" style={{ color: "var(--iggg-fg)", fontWeight: 700, fontSize: "0.9375rem" }}>
                            {total > 0 ? `${Math.round(total / 10000).toLocaleString()}만원` : "-"}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="iggg-table iggg-table--ref">
                      <thead>
                        <tr>
                          <th style={{ textAlign: "left" }}>날짜 · 현장명</th>
                          <th style={{ textAlign: "left" }}>브랜드</th>
                          <th style={{ textAlign: "right" }}>총액 (만원)</th>
                          <th style={{ textAlign: "right" }}>평수</th>
                        </tr>
                      </thead>
                      <tbody>
                        {franchiseList.map((e, i) => {
                          const pi = e.project_info || {};
                          const total = pi.total_amount || e.grand_total || 0;
                          const py = e.main_work?.area_pyeong || 0;
                          return (
                            <tr
                              key={e.id || i}
                              onClick={() => navigate(`/estimate/franchise/${e.id}`)}
                            >
                              <td style={{ fontWeight: 500 }}>
                                <span className="inline-flex items-center" style={{ gap: "0.4rem" }}>
                                  <span style={{ fontFamily: "var(--ff-mono)", fontSize: "0.72rem", color: "var(--iggg-fg-mute)", whiteSpace: "nowrap" }}>{fmtDate(getEstimateDate(e))}</span>
                                  {pi.name || e.brand_name_snapshot || "-"}
                                </span>
                              </td>
                              <td style={{ color: "var(--iggg-fg-mute)" }}>{e.brand_name_snapshot || "-"}</td>
                              <td className="num-mono" style={{ textAlign: "right" }}>
                                {total > 0 ? Math.round(total / 10000).toLocaleString() : "-"}
                              </td>
                              <td className="num-mono" style={{ textAlign: "right", color: "var(--iggg-fg-mute)" }}>
                                {py > 0 ? `${py}평` : "-"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )
              )}
            </>
          )}
        </div>
      )}

      {/* ═══ 전체 탭 ═══ */}
      {topTab === "all" && (
        <div className="p-3 pb-20">
          {mergedLoading && <div className="text-center py-16 iggg-empty__meta">불러오는 중...</div>}
          {!mergedLoading && mergedList.length === 0 && (
            <div className="iggg-empty">
              <div className="iggg-empty__mark">◇</div>
              <div>
                <div className="iggg-empty__title">등록된 견적이 없습니다</div>
                <div className="iggg-empty__meta" style={{ marginTop: 4 }}>no records</div>
              </div>
            </div>
          )}
          {!mergedLoading && mergedList.length > 0 && (
            isMobile ? (
              <div className="flex flex-col gap-2">
                {mergedList.map((item, i) => {
                  const name = item.project_info?.name || item.siteName || item.project_name || item.site_name || "-";
                  const date = (item.meta?.updated_at || item.updated_at || item.meta?.created_at || item.project_info?.date || "").substring(0, 10);
                  const total = item._type === "balance"
                    ? Math.round((item.total_amount || item.contract_amount || 0) / 10000)
                    : Math.round((item.project_info?.total_amount || item.grand_total || item.grandTotal || 0) / 10000);
                  const status = item.meta?.status || item.status || "";
                  const kind = item.doc_kind === "addendum"
                    ? { l: "추가견적", cls: "iggg-kind iggg-kind--violet" }
                    : (KIND_TAG[item._type] || { l: item._type || "-", cls: "iggg-kind iggg-kind--faint" });
                  const statusLabel = status === "confirmed" ? "확정" : status === "settled" ? "정산" : status === "paid" ? "완납" : status === "draft" ? "초안" : status || "";
                  const statusCls = (status === "confirmed" || status === "paid")
                    ? "iggg-status iggg-status--paid"
                    : status === "settled" ? "iggg-status iggg-status--settle" : "iggg-status iggg-status--draft";
                  return (
                    <div key={item.id || i}
                      onClick={() => {
                        if (item._type === "simple") navigate(`/estimate/simple?id=${item.id}`);
                        else if (item._type === "simple_v2") navigate(`/estimate/simple-v2?id=${item.id}`);
                        else if (item._type === "balance") navigate(`/balance/${item.id}`);
                        else if (item._type === "franchise") navigate(`/estimate/franchise/${item.id}`);
                        else navigate(`/estimate/${item.id}`);
                      }}
                      className="iggg-card iggg-card--interactive"
                      style={{ cursor: "pointer" }}>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="iggg-card-title flex items-center gap-1.5 flex-wrap min-w-0">
                          <span className="truncate">{name}</span>
                          <span className={`shrink-0 ${kind.cls}`}>{kind.l}</span>
                        </div>
                        {statusLabel && (
                          <span className={`shrink-0 ${statusCls}`}>
                            {statusLabel}
                          </span>
                        )}
                      </div>
                      <div className="iggg-card-meta mb-2">{date || "-"}</div>
                      <div className="iggg-card-meta">총액 · total</div>
                      <div className="num-mono" style={{ color: "var(--iggg-fg)", fontWeight: 700, fontSize: "0.9375rem", marginTop: 2 }}>
                        {total > 0 ? `${total.toLocaleString()}만원` : "-"}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
            <div className="overflow-x-auto">
              <table className="iggg-table">
                <thead>
                  <tr>
                    <th style={{ textAlign: "left" }}>현장명 · site</th>
                    <th style={{ textAlign: "right" }}>총액 (만원)</th>
                    <th style={{ textAlign: "center" }}>상태</th>
                    <th style={{ textAlign: "right" }}>날짜</th>
                  </tr>
                </thead>
                <tbody>
                  {mergedList.map((item, i) => {
                    const name = item.project_info?.name || item.siteName || item.project_name || item.site_name || "-";
                    const date = (item.meta?.updated_at || item.updated_at || item.meta?.created_at || item.project_info?.date || "").substring(0, 10);
                    const total = item._type === "balance"
                      ? Math.round((item.total_amount || item.contract_amount || 0) / 10000)
                      : Math.round((item.project_info?.total_amount || item.grand_total || item.grandTotal || 0) / 10000);
                    const status = item.meta?.status || item.status || "";
                    const kind = item.doc_kind === "addendum"
                      ? { l: "추가견적", cls: "iggg-kind iggg-kind--violet" }
                      : (KIND_TAG[item._type] || { l: item._type, cls: "iggg-kind iggg-kind--faint" });
                    const statusCls = (status === "confirmed" || status === "paid")
                      ? "iggg-status iggg-status--paid"
                      : status === "settled" ? "iggg-status iggg-status--settle" : "iggg-status iggg-status--draft";
                    return (
                      <tr key={item.id || i}
                        onClick={() => {
                          if (item._type === "simple") navigate(`/estimate/simple?id=${item.id}`);
                          else if (item._type === "simple_v2") navigate(`/estimate/simple-v2?id=${item.id}`);
                          else if (item._type === "balance") navigate(`/balance/${item.id}`);
                          else if (item._type === "franchise") navigate(`/estimate/franchise/${item.id}`);
                          else navigate(`/estimate/${item.id}`);
                        }}>
                        <td style={{ fontWeight: 500 }}>
                          {name}
                          <span className={kind.cls} style={{ marginLeft: 6, verticalAlign: "middle" }}>{kind.l}</span>
                        </td>
                        <td className="num-mono" style={{ textAlign: "right" }}>{total > 0 ? total.toLocaleString() : "-"}</td>
                        <td style={{ textAlign: "center" }}>
                          <span className={statusCls}>
                            {status === "confirmed" ? "확정" : status === "settled" ? "정산" : status === "paid" ? "완납" : status === "draft" ? "초안" : status || "-"}
                          </span>
                        </td>
                        <td className="num-mono" style={{ textAlign: "right", color: "var(--iggg-fg-mute)", fontSize: "0.75rem" }}>{date || "-"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            )
          )}
        </div>
      )}

      {/* ═══ 간이견적 탭 ═══ */}
      {topTab === "simple" && (
        <div className="p-3 pb-20">
          {/* 현장관리에서 온 추가공사 후보 */}
          {extraWorks.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--ds-mute)", marginBottom: 8 }}>
                현장관리 · 추가공사 후보 {extraWorks.length}건
              </div>
              <div className="flex flex-col gap-2">
                {extraWorks.map((w, i) => (
                  <div key={i} className="iggg-card" style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700 }}>{w.siteName} — {w.desc}</div>
                      <div className="num-mono" style={{ fontSize: 12, color: "var(--ds-mute)" }}>
                        {w.tbd ? "금액미정" : `${(w.amount || 0).toLocaleString()}원`}
                        {w.confirmed ? " · 점주확인" : ""}{w.date ? ` · ${w.date}` : ""}
                      </div>
                    </div>
                    <button className="ds-btn ds-btn-sm ds-btn-outline"
                      onClick={() => navigate(`/estimate/simple?site=${encodeURIComponent(w.siteName)}&work=${encodeURIComponent(w.desc)}${w.tbd ? "" : `&amount=${w.amount || 0}`}`)}>
                      간이견적 작성
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* 종류 필터 — 상시 노출(0건이어도 진입점 역할, 사용자 피드백 2026-07-22) */}
          {!simpleLoading && simpleList.length > 0 && (
            <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
              {[["all", `전체 (${simpleList.length})`],
                ["simple", `간이 (${simpleList.length - simpleAddendumCount})`],
                ["addendum", `추가견적 (${simpleAddendumCount})`]].map(([k, label]) => (
                <button key={k} type="button"
                        className={`ds-btn ds-btn-sm ${simpleKindFilter === k ? "ds-btn-outline" : "ds-btn-ghost"}`}
                        aria-pressed={simpleKindFilter === k}
                        onClick={() => setSimpleKindFilter(k)}>
                  {label}
                </button>
              ))}
            </div>
          )}
          {simpleLoading && <div className="text-center py-16 iggg-empty__meta">불러오는 중...</div>}
          {!simpleLoading && simpleList.length > 0 && simpleShown.length === 0 && (
            <div className="iggg-empty">
              <div className="iggg-empty__mark">▤</div>
              <div>
                <div className="iggg-empty__title">
                  {simpleKindFilter === "addendum" ? "추가견적서가 없습니다" : "간이견적서가 없습니다"}
                </div>
                {simpleKindFilter === "addendum" && (
                  <div className="iggg-empty__meta" style={{ marginTop: 4 }}>
                    추가 공사 내역 — 전자계약 [추가내역서 발송]의 원본 문서
                  </div>
                )}
              </div>
              {simpleKindFilter === "addendum" && (
                <button onClick={() => navigate("/estimate/simple?kind=addendum")} className="ds-btn ds-btn-primary">
                  + 추가견적서 작성
                </button>
              )}
            </div>
          )}
          {!simpleLoading && simpleList.length === 0 && (
            <div className="iggg-empty">
              <div className="iggg-empty__mark">▤</div>
              <div>
                <div className="iggg-empty__title">아직 간이견적서가 없습니다</div>
                <div className="iggg-empty__meta" style={{ marginTop: 4 }}>start a new simple estimate</div>
              </div>
              <button onClick={() => navigate("/estimate/simple")} className="ds-btn ds-btn-primary">+ 간이견적 작성</button>
            </div>
          )}
          {!simpleLoading && simpleShown.length > 0 && (
            isMobile ? (
              <div className="flex flex-col gap-2">
                {simpleShown.map((s, i) => {
                  const isV2 = s._type === "simple_v2";
                  const pi = s.project_info || {};
                  const name = pi.name || s.siteName || s.project_name || "-";
                  const total = s.grand_total || 0;
                  const itemCount = (s.sections || []).reduce((sum, sec) => sum + (sec.items?.length || 0), 0);
                  const date = pi.date || s.date || (s.updated_at || "").slice(0, 10) || "-";
                  const route = isV2 ? `/estimate/simple-v2?id=${s.id}` : `/estimate/simple?id=${s.id}`;
                  const deleteEndpoint = isV2
                    ? `${API_BASE_URL}/api/simple-estimates-v2/${s.id}`
                    : `${API_BASE_URL}/api/simple-estimates/${s.id}`;
                  return (
                    <div key={s.id || i}
                      onClick={() => navigate(route)}
                      className="iggg-card iggg-card--interactive"
                      style={{ cursor: "pointer" }}>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="iggg-card-title flex-1 min-w-0 flex items-center gap-1.5 flex-wrap">
                          <span style={{ fontFamily: "var(--ff-mono)", fontSize: "0.72rem", color: "var(--iggg-fg-mute)", whiteSpace: "nowrap" }}>{fmtDate(date)}</span>
                          <EditableName
                            id={s.id}
                            value={name}
                            save={(v) => saveSimpleName(s.id, v, s._type)}
                            onSaved={(nm) => setSimpleList(prev => prev.map(x => x.id === s.id ? { ...x, project_info: { ...(x.project_info || {}), name: nm } } : x))}
                          />
                          <span className={`iggg-kind ${s.doc_kind === "addendum" ? "iggg-kind--violet" : "iggg-kind--green"} shrink-0`}>{s.doc_kind === "addendum" ? "추가견적" : (isV2 ? "간이 v2" : "간이")}</span>
                        </div>
                        <button onClick={async (e) => {
                          e.stopPropagation();
                          if (!window.confirm(`'${name}' ${isV2 ? "간이 v2" : "간이"} 견적을 삭제하시겠습니까?\n(휴지통에서 7일간 복원 가능)`)) return;
                          try {
                            const res = await apiFetch(deleteEndpoint, { method: "DELETE" });  // (전수검증)
                            if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || `삭제 실패 (${res.status})`); }
                            setSimpleList(prev => prev.filter(x => x.id !== s.id));
                          } catch (err) { alert(friendlyErrorMessage(err, "삭제에 실패했습니다.")); }
                        }} className="iggg-btn iggg-btn--ghost iggg-btn--cap shrink-0"
                           style={{ color: "var(--iggg-danger)", borderColor: "rgba(255,107,94,0.35)" }}>삭제</button>
                      </div>
                      <div className="iggg-card-meta">총액 · total</div>
                      <div className="num-mono" style={{ color: "var(--iggg-fg)", fontWeight: 700, fontSize: "0.9375rem", marginTop: 2 }}>
                        {total > 0 ? `${fmt(total)}만원` : "-"}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
            <div className="overflow-x-auto">
              <table className="iggg-table">
                <thead>
                  <tr>
                    <th style={{ textAlign: "left" }}>날짜 · 현장명</th>
                    <th style={{ textAlign: "right" }}>총액 (만원)</th>
                    <th style={{ textAlign: "right" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {simpleShown.map((s, i) => {
                    const isV2 = s._type === "simple_v2";
                    const pi = s.project_info || {};
                    const name = pi.name || s.siteName || s.project_name || "-";
                    const total = s.grand_total || 0;
                    const itemCount = (s.sections || []).reduce((sum, sec) => sum + (sec.items?.length || 0), 0);
                    const date = pi.date || s.date || (s.updated_at || "").slice(0, 10) || "-";
                    const route = isV2 ? `/estimate/simple-v2?id=${s.id}` : `/estimate/simple?id=${s.id}`;
                    const deleteEndpoint = isV2
                      ? `${API_BASE_URL}/api/simple-estimates-v2/${s.id}`
                      : `${API_BASE_URL}/api/simple-estimates/${s.id}`;
                    return (
                      <tr key={s.id || i} onClick={() => navigate(route)}>
                        <td style={{ fontWeight: 500 }}>
                          <span className="inline-flex items-center" style={{ gap: "0.4rem" }}>
                            <span style={{ fontFamily: "var(--ff-mono)", fontSize: "0.72rem", color: "var(--iggg-fg-mute)", whiteSpace: "nowrap" }}>{fmtDate(date)}</span>
                            <EditableName
                              id={s.id}
                              value={name}
                              save={(v) => saveSimpleName(s.id, v, s._type)}
                              onSaved={(nm) => setSimpleList(prev => prev.map(x => x.id === s.id ? { ...x, project_info: { ...(x.project_info || {}), name: nm } } : x))}
                            />
                            <span className={`iggg-kind ${s.doc_kind === "addendum" ? "iggg-kind--violet" : "iggg-kind--green"}`} style={{ verticalAlign: "middle" }}>{s.doc_kind === "addendum" ? "추가견적" : (isV2 ? "간이 v2" : "간이")}</span>
                          </span>
                        </td>
                        <td className="num-mono" style={{ textAlign: "right" }}>{total > 0 ? fmt(total) : "-"}</td>
                        <td style={{ textAlign: "right" }}>
                          <button onClick={async (e) => {
                            e.stopPropagation();
                            if (!window.confirm(`'${name}' ${isV2 ? "간이 v2" : "간이"} 견적을 삭제하시겠습니까?\n(휴지통에서 7일간 복원 가능)`)) return;
                            try {
                              const res = await apiFetch(deleteEndpoint, { method: "DELETE" });  // (전수검증)
                              if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || `삭제 실패 (${res.status})`); }
                              setSimpleList(prev => prev.filter(x => x.id !== s.id));
                            } catch (err) { alert(friendlyErrorMessage(err, "삭제에 실패했습니다.")); }
                          }} className="iggg-btn iggg-btn--ghost iggg-btn--cap"
                             style={{ color: "var(--iggg-danger)", borderColor: "rgba(255,107,94,0.35)" }}>삭제</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            )
          )}
        </div>
      )}

      {/* ═══ 잔금내역서 탭 ═══ */}
      {topTab === "balance" && (
        <div className="p-3 pb-20">
          {/* 생성예정 — 계약서(ig-contract) 작성됐으나 잔금내역서 미생성 현장 */}
          {(upcomingList.length > 0 || hiddenContracts.length > 0) && (
            <div style={{ marginBottom: 16 }}>
              {upcomingList.length > 0 && (
                <>
                  <div className="iggg-empty__meta" style={{ marginBottom: 8, fontWeight: 600 }}>
                    생성예정 · 계약서 작성됨 ({upcomingList.length})
                  </div>
                  <div className="flex flex-col gap-2">
                    {upcomingList.map((u) => {
                      const days = (u.start_date && u.end_date)
                        ? periodDays(u.start_date, u.end_date)
                        : null;
                      const sched = (u.start_date && u.end_date)
                        ? `${u.start_date} ~ ${u.end_date}${days != null ? ` · ${days}일` : ""}`
                        : (u.sign_date ? `계약일 ${u.sign_date}` : "일정 미정");
                      return (
                        <div key={u.contract_id} className="iggg-card"
                          style={{ borderStyle: "dashed", display: "flex", alignItems: "center", gap: 10 }}>
                          <button onClick={() => hideContract(u.contract_id, u.site_name)}
                            title="생성예정 목록에서 숨김"
                            className="ds-btn ds-btn-ghost ds-btn-sm"
                            style={{ minWidth: 30, padding: "2px 8px", color: "var(--ds-mute)" }}>H</button>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="iggg-card-title">{u.site_name}</div>
                            <div className="iggg-card-meta" style={{ marginTop: 2 }}>
                              📅 {sched} · 계약 {fmt(u.amt_supply)}만원
                            </div>
                          </div>
                          <button onClick={() => createFromContract(u.contract_id)}
                            className="ds-btn ds-btn-primary ds-btn-sm">작성</button>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
              {hiddenContracts.length > 0 && (
                <div style={{ marginTop: upcomingList.length > 0 ? 8 : 0 }}>
                  <button onClick={() => setShowHidden(v => !v)}
                    className="ds-btn ds-btn-ghost ds-btn-sm"
                    style={{ fontSize: 12, color: "var(--ds-mute)" }}>
                    {showHidden ? "▾" : "▸"} 숨긴 현장 {hiddenContracts.length}
                  </button>
                  {showHidden && (
                    <div className="flex flex-col gap-1" style={{ marginTop: 6 }}>
                      {hiddenContracts.map((h) => (
                        <div key={h.contract_id} className="flex items-center justify-between gap-2"
                          style={{ padding: "4px 10px", fontSize: 13, color: "var(--ds-mute)" }}>
                          <span>{h.site_name || h.contract_id}</span>
                          <button onClick={() => unhideContract(h.contract_id, h.site_name)}
                            className="ds-btn ds-btn-outline ds-btn-sm">되돌리기</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          {balanceLoading && <div className="text-center py-16 iggg-empty__meta">불러오는 중...</div>}
          {!balanceLoading && balanceList.length === 0 && upcomingList.length === 0 && (
            <div className="iggg-empty">
              <div className="iggg-empty__mark">₩</div>
              <div>
                <div className="iggg-empty__title">아직 잔금내역서가 없습니다</div>
                <div className="iggg-empty__meta" style={{ marginTop: 4 }}>start a new balance sheet</div>
              </div>
              <button onClick={() => navigate("/balance/new")} className="ds-btn ds-btn-primary">+ 잔금내역서 작성</button>
            </div>
          )}
          {!balanceLoading && balanceList.length > 0 && (
            isMobile ? (
              <div className="flex flex-col gap-2">
                {balanceList.map((b, i) => {
                  const contract = b.contract_amount || 0;
                  const paid = b.total_paid || (b.payments || []).reduce((s, p) => s + (Number(p.amount) || 0), 0);
                  const remaining = b.balance != null ? b.balance : (Math.round((contract + (b.adjustments || []).reduce((s, a) => s + (Number(a.amount) || 0), 0)) * 1.1) - paid);
                  const isUnpaid = remaining > 0;
                  const statusLabel = b.status === "paid" ? "완납" : b.status === "draft" ? "미확정" : "미수금";
                  const statusCls = (b.status === "paid" || remaining <= 0)
                    ? "iggg-status iggg-status--paid"
                    : b.status === "draft" ? "iggg-status iggg-status--draft" : "iggg-status iggg-status--unpaid";
                  return (
                    <div key={b.id || i}
                      onClick={() => navigate(`/balance/${b.id}`)}
                      className="iggg-card iggg-card--interactive"
                      style={{ cursor: "pointer" }}>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="iggg-card-title flex-1 min-w-0 flex items-center gap-1.5 flex-wrap">
                          <span style={{ fontFamily: "var(--ff-mono)", fontSize: "0.72rem", color: "var(--iggg-fg-mute)", whiteSpace: "nowrap" }}>{fmtDate(b.date)}</span>
                          <EditableName
                            id={b.id}
                            value={b.project_name || b.site_name || ""}
                            save={(v) => saveBalanceName(b.id, v)}
                            onSaved={(nm) => setBalanceList(prev => prev.map(x => x.id === b.id ? { ...x, project_name: nm } : x))}
                          />
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <button onClick={async (e) => {
                            e.stopPropagation();
                            if (!window.confirm(`'${b.project_name || b.site_name || ""}' 잔금내역서를 삭제하시겠습니까?\n(휴지통에서 7일간 복원 가능)`)) return;
                            try {
                              const res = await apiFetch(`${API_BASE_URL}/api/balance-sheets/${b.id}`, { method: "DELETE" });  // (전수검증)
                              if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || `삭제 실패 (${res.status})`); }
                              setBalanceList(prev => prev.filter(x => x.id !== b.id));
                            } catch (err) { alert(friendlyErrorMessage(err, "삭제에 실패했습니다.")); }
                          }} className="iggg-btn iggg-btn--ghost iggg-btn--cap"
                             style={{ color: "var(--iggg-danger)", borderColor: "rgba(255,107,94,0.35)" }}>삭제</button>
                        </div>
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <div className="flex items-center justify-between">
                          <span className="iggg-card-meta">잔금 · balance</span>
                          <span className={`num-mono ${isUnpaid ? "text-[var(--ds-danger)]" : "text-[var(--ds-success)]"}`}
                                style={{ fontWeight: 700, fontSize: "0.9375rem" }}>
                            {`${fmt(remaining)}만원`}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
            <div className="overflow-x-auto">
              <table className="iggg-table">
                <thead>
                  <tr>
                    <th style={{ textAlign: "left" }}>날짜 · 현장명</th>
                    <th style={{ textAlign: "right" }}>잔금 (만원)</th>
                    <th style={{ textAlign: "right" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {balanceList.map((b, i) => {
                    const contract = b.contract_amount || 0;
                    const paid = b.total_paid || (b.payments || []).reduce((s, p) => s + (Number(p.amount) || 0), 0);
                    const remaining = b.balance != null ? b.balance : (Math.round((contract + (b.adjustments || []).reduce((s, a) => s + (Number(a.amount) || 0), 0)) * 1.1) - paid);
                    const statusCls = (b.status === "paid" || remaining <= 0)
                      ? "iggg-status iggg-status--paid"
                      : b.status === "draft" ? "iggg-status iggg-status--draft" : "iggg-status iggg-status--unpaid";
                    return (
                      <tr key={b.id || i} onClick={() => navigate(`/balance/${b.id}`)}>
                        <td style={{ fontWeight: 500 }}>
                          <span className="inline-flex items-center" style={{ gap: "0.4rem" }}>
                            <span style={{ fontFamily: "var(--ff-mono)", fontSize: "0.72rem", color: "var(--iggg-fg-mute)", whiteSpace: "nowrap" }}>{fmtDate(b.date)}</span>
                            <EditableName
                              id={b.id}
                              value={b.project_name || b.site_name || ""}
                              save={(v) => saveBalanceName(b.id, v)}
                              onSaved={(nm) => setBalanceList(prev => prev.map(x => x.id === b.id ? { ...x, project_name: nm } : x))}
                            />
                          </span>
                        </td>
                        <td className={`num-mono ${remaining > 0 ? "text-[var(--ds-danger)]" : "text-[var(--ds-success)]"}`}
                            style={{ textAlign: "right", fontWeight: 600 }}>{fmt(remaining)}</td>
                        <td style={{ textAlign: "right" }}>
                          <button onClick={async (e) => {
                            e.stopPropagation();
                            if (!window.confirm(`'${b.project_name || b.site_name || ""}' 잔금내역서를 삭제하시겠습니까?\n(휴지통에서 7일간 복원 가능)`)) return;
                            try {
                              const res = await apiFetch(`${API_BASE_URL}/api/balance-sheets/${b.id}`, { method: "DELETE" });  // (전수검증)
                              if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || `삭제 실패 (${res.status})`); }
                              setBalanceList(prev => prev.filter(x => x.id !== b.id));
                            } catch (err) { alert(friendlyErrorMessage(err, "삭제에 실패했습니다.")); }
                          }} className="iggg-btn iggg-btn--ghost iggg-btn--cap"
                             style={{ color: "var(--iggg-danger)", borderColor: "rgba(255,107,94,0.35)" }}>삭제</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            )
          )}
        </div>
      )}

      </div>{/* end 탭 콘텐츠 영역 */}

      {/* 푸터(LINKS 레퍼런스 톤) — 점선 위 + 작은 mono */}
      <div className="iggg-footer iggg-mobile-tight">
        <span>이견공간 · iggg studio</span>
        <span>internal use only</span>
        <span>©{new Date().getFullYear()}</span>
      </div>

    </div>
  );
}
